/**
 * ISO 3166-1 alpha-2 country codes, the vocabulary the backend validates against (green coffee origins,
 * supplier addresses). Names are not stored here: `Intl.DisplayNames` renders each code in whatever
 * language the user is reading the UI in, so the list never needs translating.
 */
const ISO_3166_ALPHA2 =
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO " +
  "FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE " +
  "JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO " +
  "MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW " +
  "PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM " +
  "TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW";

export const COUNTRY_CODES: readonly string[] = ISO_3166_ALPHA2.split(" ");

/**
 * The coffee producing countries a roastery picks from most of the time. They are offered first in
 * pickers; the full list stays available underneath.
 */
export const COFFEE_ORIGIN_CODES: readonly string[] = [
  "BI", "BO", "BR", "CD", "CN", "CO", "CR", "DO", "EC", "ET", "GT", "HN", "HT", "ID", "IN", "JM", "KE",
  "LA", "MM", "MW", "MX", "NI", "PA", "PE", "PG", "PH", "RW", "SV", "TH", "TL", "TZ", "UG", "VN", "YE", "ZM",
];

/** Localised country name for an ISO code, falling back to the code itself for unknown values. */
export function countryName(code: string | null | undefined, locale: string): string {
  if (!code) {
    return "";
  }
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Country codes sorted by their name in `locale` — the order a picker should show them in.
 *
 * <p>`without` takes codes out of the result, which is what a picker that offers the coffee origins
 * in a group of their own needs: otherwise Brazil is in the list twice, once in each group, and the
 * second one looks like a different country until you read it (US-23.6).
 */
export function sortedCountries(
  locale: string,
  codes: readonly string[] = COUNTRY_CODES,
  without: readonly string[] = [],
) {
  const collator = new Intl.Collator(locale);
  const excluded = new Set(without.map((code) => code.toUpperCase()));
  return codes
    .filter((code) => !excluded.has(code.toUpperCase()))
    .map((code) => ({ code, name: countryName(code, locale) }))
    .sort((a, b) => collator.compare(a.name, b.name));
}
